import React from "react";
//#region types
import { PersonSalaryPendingApprovalListProps } from "models/propTypes/salaryPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { StyledDateRange } from "models/propTypes/commonPropTypes";
import { CurrencyEnums, StyleEnums } from "models/enums";
import { User } from "models/propTypes/userProfilePropTypes";
import { NestedTableRowCommonKeys, TableRowProperties } from "models/propTypes/simpleTableProps";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import MonthlySalaryInput from "./monthlySalaryInput";
import { Icon, NestedTable, TableAsync } from "components/common";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const PersonSalaryPendingApprovalList: React.FC<PersonSalaryPendingApprovalListProps> = (props) => {
  const onRowsUpdate = React.useCallback(
    (
      previous: (WintSuperkollAPIModel.PersonSalaryRow & NestedTableRowCommonKeys)[],
      next: (WintSuperkollAPIModel.PersonSalaryRow & NestedTableRowCommonKeys)[]
    ) => {
      next.forEach((n) => {
        const prevRow = previous.find((p) => p.id === n.id);
        if (prevRow) {
          n.selected = prevRow.selected;
          n.expanded = prevRow.expanded;
        }
      });

      return next;
    },
    []
  );

  const getBaseProperties = (): Partial<Record<keyof WintSuperkollAPIModel.PersonSalaryRow, TableRowProperties>> => {
    return {
      description: {
        description: TranslationKeys.Common.Name,
        formatter: (row: WintSuperkollAPIModel.PersonSalaryRow) =>
          row.type === WintSuperkollAPIModel.SalaryRowType.Summary
            ? new User(row.personId, row.description, false, true)
            : row.description,
        maxWidth: 180,
        noWrap: true,
        className: cssExports.freezeCell,
      },
      from: {
        description: TranslationKeys.Common.Period,
        formatter: (row: WintSuperkollAPIModel.PersonSalaryRow) =>
          row.from || row.to ? new StyledDateRange(row.from, row.to) : "",
        noWrap: true,
      },
      grossAmount: {
        description: TranslationKeys.Salary.GrossSalary,
        formatter: (row: WintSuperkollAPIModel.PersonSalaryRow) =>
          row.type === WintSuperkollAPIModel.SalaryRowType.MonthlySalary || !props.isAutoCreatedSimpleSalary ? (
            <MonthlySalaryInput
              key={row.id}
              amount={row.grossAmount ?? 0}
              currency={CurrencyEnums.CurrencyCode.SEK}
              onChange={props.onMonthlySalaryChange.bind(null, row.id, row.personId ?? 0)}
            />
          ) : row.grossAmount !== undefined ? (
            <Grid container spacing={1} wrap="nowrap" justifyContent="flex-end" maxWidth={150}>
              {row.type === WintSuperkollAPIModel.SalaryRowType.Summary && (
                <Grid item>
                  <Icon icon={IconDefinitionConstants.WEdit} displayType={StyleEnums.DisplayType.Info} />
                </Grid>
              )}
              <Grid item className={cssExports.textBlue}>
                {LocalizationHelperInstance.getFormattedCurrency(row.grossAmount, CurrencyEnums.CurrencyCode.SEK)}
              </Grid>
            </Grid>
          ) : (
            ""
          ),
        expandOnClick: true,
        className: clsx(cssExports.textBlack, cssExports.personSalaryAmountColumn),
      },
    };
  };

  const getTable = (): JSX.Element => {
    return (
      <TableAsync<WintSuperkollAPIModel.PersonSalaryRow>
        properties={getBaseProperties()}
        allowCheckBoxSelection
        filter={props.rows ?? []}
        onRowSelectionsChange={props.onSalaryEntriesSelect}
        disableQueryPagination
        loading={props.rows === undefined}
      />
    );
  };

  const getNestedTable = (): JSX.Element => {
    return (
      <NestedTable<WintSuperkollAPIModel.PersonSalaryRow, any>
        properties={{
          ...getBaseProperties(),
          taxAmount: {
            description: TranslationKeys.Salary.Tax,
            formatter: (row: WintSuperkollAPIModel.PersonSalaryRow) =>
              row.taxAmount !== undefined
                ? LocalizationHelperInstance.getFormattedCurrency(row.taxAmount, CurrencyEnums.CurrencyCode.SEK)
                : "",
            alignment: "right",
            className: cssExports.textBlack,
          },
          netAmount: {
            description: TranslationKeys.Salary.NetSalary,
            formatter: (row: WintSuperkollAPIModel.PersonSalaryRow) =>
              row.netAmount !== undefined
                ? LocalizationHelperInstance.getFormattedCurrency(row.netAmount, CurrencyEnums.CurrencyCode.SEK)
                : "",
            alignment: "right",
            className: cssExports.textBlack,
          },
        }}
        rows={props.rows ?? []}
        childrenKey={"children"}
        allowCheckBoxSelection
        checkBoxesCheckedAsDefault={props.checkBoxesCheckedAsDefault}
        onRowSelectionsChange={props.onSalaryEntriesSelect}
        onRowsUpdate={onRowsUpdate}
        loading={props.rows === undefined}
      />
    );
  };

  return props.isAutoCreatedSimpleSalary ? getNestedTable() : getTable();
};

export default PersonSalaryPendingApprovalList;
